import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { authService } from '@/services/auth';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '@/components/LoadingSpinner';
import { PlatformService } from '@/utils/platform';
import { formatExpiryWindow } from '@/utils/dateFormat';

interface InvitationData {
  email: string;
  role: string;
  organizationName: string;
  expiresAt: string;
  invitedBy: string;
}

const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [legalAccepted, setLegalAccepted] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    profile: {
      title: '',
      department: '',
      licenseNumber: '',
      phoneNumber: '',
      specialties: [] as string[]
    }
  });

  useEffect(() => {
    if (!token) {
      setValidationError('Invalid invitation link - no token provided');
      setLoading(false);
      return;
    }

    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    try {
      const result = await authService.validateInvitation(token!);

      if (result.valid && result.email) {
        // The backend returns the invitation data directly, not nested
        setInvitation({
          email: result.email!,
          role: result.role!,
          organizationName: result.organizationName!,
          expiresAt: result.expiresAt!,
          invitedBy: result.invitedBy!
        });
      } else {
        setValidationError(result.error || 'Invalid or expired invitation');
      }
    } catch (error) {
      console.error('Invitation validation error:', error);
      // A refused invitation (expired, already used) arrives as a 400 carrying the reason.
      const reason = axios.isAxiosError(error) ? error.response?.data?.error : undefined;
      setValidationError(reason || 'Failed to validate invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!legalAccepted) {
      toast.error('You must accept the Terms of Service, Privacy Policy, and EULA to continue');
      return;
    }

    setSubmitting(true);
    try {
      await authService.acceptInvitation({
        token: token!,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        profile: formData.profile,
        legalAccepted: true
      });

      toast.success('Account created successfully! Welcome to the team!');
      navigate('/dashboard');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create account';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateProfileField = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        [field]: value
      }
    }));
  };

  const isProviderRole = invitation?.role === 'operator' || invitation?.role === 'admin';

  if (loading) {
    return <LoadingSpinner />;
  }

  if (validationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Invalid Invitation
            </h2>
            <p className="mt-2 text-sm text-red-600">
              {validationError}
            </p>
            <div className="mt-4">
              <button
                onClick={() => {
                  const loginPath = PlatformService.isNative() ? '/mobile-login' : '/login';
                  navigate(loginPath);
                }}
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return <LoadingSpinner />;
  }

  const expiresAt = new Date(invitation.expiresAt);
  const timeLeft = expiresAt.getTime() - Date.now();
  const hoursLeft = Math.max(0, Math.floor(timeLeft / (1000 * 60 * 60)));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Join {invitation.organizationName}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            You've been invited by {invitation.invitedBy} to join as a <span className="font-medium">{invitation.role}</span>
          </p>
          <p className="mt-1 text-xs text-gray-500">
            This invitation expires in {formatExpiryWindow(hoursLeft)}
          </p>
        </div>

        {/* Invitation Details */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Account Details
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p><strong>Email:</strong> {invitation.email}</p>
                <p><strong>Role:</strong> {invitation.role}</p>
                <p><strong>Organization:</strong> {invitation.organizationName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Account Setup Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Personal Information */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Complete Your Profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">
                  First Name *
                </label>
                <input
                  id="firstName"
                  type="text"
                  required
                  className="input-field"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName" className="form-label">
                  Last Name *
                </label>
                <input
                  id="lastName"
                  type="text"
                  required
                  className="input-field"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                />
              </div>

              <div className="md:col-span-2 form-group">
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-field pr-10"
                    placeholder="Create a secure password"
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Password must be at least 8 characters long
                </p>
              </div>

              <div className="md:col-span-2 form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password *
                </label>
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="input-field"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateField('confirmPassword', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Professional Information (for providers) */}
          {isProviderRole && (
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Professional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label htmlFor="title" className="form-label">
                    Professional Title
                  </label>
                  <input
                    id="title"
                    type="text"
                    className="input-field"
                    placeholder="e.g., MD, RN, PA"
                    value={formData.profile.title}
                    onChange={(e) => updateProfileField('title', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="department" className="form-label">
                    Department
                  </label>
                  <input
                    id="department"
                    type="text"
                    className="input-field"
                    placeholder="e.g., Cardiology, Emergency"
                    value={formData.profile.department}
                    onChange={(e) => updateProfileField('department', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="licenseNumber" className="form-label">
                    License Number
                  </label>
                  <input
                    id="licenseNumber"
                    type="text"
                    className="input-field"
                    placeholder="Professional license number"
                    value={formData.profile.licenseNumber}
                    onChange={(e) => updateProfileField('licenseNumber', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber" className="form-label">
                    Phone Number
                  </label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    className="input-field"
                    placeholder="(555) 123-4567"
                    value={formData.profile.phoneNumber}
                    onChange={(e) => updateProfileField('phoneNumber', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Legal Acceptance */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Legal Agreements</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300"
                style={{ accentColor: '#1e4a7a' }}
              />
              <span className="text-sm text-gray-600 leading-relaxed">
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500 underline">
                  Terms of Service
                </a>,{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500 underline">
                  Privacy Policy
                </a>, and{' '}
                <a href="/eula" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500 underline">
                  End User License Agreement
                </a>.
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={submitting || !legalAccepted}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating Account...' : 'Create Account & Join'}
            </button>
          </div>
        </form>

        {/* Security Notice */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800 text-center">
            🔒 All data is encrypted and HIPAA compliant. Your account will be created with {invitation.role} permissions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;